import { useState } from 'react'
import ModalBackdrop from './ModalBackdrop'
import type { ChipDefinition, ChipPlayConfirmation } from '../types'
import type { ChipStatus } from './ChipRow'
import { useRulesScreen } from '../features/rules/rulesContext'
import SeeAllRulesRow from '../features/rules/SeeAllRulesRow'
import './ChipInfoModal.css'

interface ChipInfoModalProps {
  chip: ChipDefinition
  status: ChipStatus
  usesRemaining: number
  seasonCap: number
  multiplierDescription: string
  currentGameweek: number
  // After the first use, the second unlocks at this gameweek.
  secondUseUnlockGameweek?: number
  // A chip-specific gameweek before which it can't be played at all (no chip currently uses this).
  unlockGameweek?: number
  // Name of the chip currently active this gameweek that's blocking this one, if any.
  blockingChipName?: string
  // Whether this gameweek's deadline has passed, freezing the chip's current state.
  deadlinePassed: boolean
  // Present for a chip whose play needs a second, explicit tap. Absent means
  // the play button calls onPlay directly, which is every chip that can't cost you anything.
  playConfirmation?: ChipPlayConfirmation
  onPlay: () => void
  onCancel: () => void
  onClose: () => void
}

// "both"/"your" read better than "all 2"/"all 1" and keep Double Up's copy unchanged.
function allUsesLabel(seasonCap: number): string {
  if (seasonCap === 1) return 'your'
  return seasonCap === 2 ? 'both' : `all ${seasonCap}`
}

function ChipInfoModal({
  chip,
  status,
  usesRemaining,
  seasonCap,
  currentGameweek,
  secondUseUnlockGameweek,
  unlockGameweek,
  blockingChipName,
  deadlinePassed,
  playConfirmation,
  onPlay,
  onCancel,
  onClose,
}: ChipInfoModalProps) {
  // No reset needed: the grid renders this modal conditionally, so closing it — by the Close
  // button or a backdrop click — unmounts it and drops the confirm step with it.
  const [confirming, setConfirming] = useState(false)
  const { open: openRules } = useRulesScreen()
  const confirmStep = confirming && status === 'available' ? playConfirmation : undefined

  // The confirm is a question, not a second explanation: the chip's name and description are
  // deliberately gone here, so the only thing on screen is what you're about to commit to.
  if (confirmStep) {
    return (
      <ModalBackdrop onClose={onClose}>
        <p className="chip-modal__title">{confirmStep.title}</p>
        <p className="chip-modal__description">{confirmStep.body}</p>
        {/* Two buttons, not one full-width confirm: the second tap has to land somewhere the
            first one didn't, or a double-tap sails straight through the guard. */}
        <div className="chip-modal__actions">
          <button
            className="chip-modal__action chip-modal__action--cancel"
            onClick={() => setConfirming(false)}
          >
            Back
          </button>
          <button className="chip-modal__action chip-modal__action--play" onClick={onPlay}>
            {confirmStep.confirmLabel}
          </button>
        </div>
        <button className="chip-modal__close" onClick={onClose}>
          Close
        </button>
      </ModalBackdrop>
    )
  }

  return (
    <ModalBackdrop onClose={onClose}>
      <p className="chip-modal__title">{chip.name}</p>
      <p className="chip-modal__description">{chip.description}</p>

      {!chip.implemented && <p className="chip-modal__status">Coming soon</p>}

      {chip.implemented && status === 'available' && (
        <>
          <div className="chip-modal__uses">
            {Array.from({ length: seasonCap }).map((_, index) => (
              <span
                key={index}
                className={`chip-modal__use-dot ${
                  index < seasonCap - usesRemaining ? 'chip-modal__use-dot--used' : ''
                }`}
                aria-hidden="true"
              />
            ))}
            <span className="chip-modal__uses-label">
              {usesRemaining} of {seasonCap} uses left this season
            </span>
          </div>
          {secondUseUnlockGameweek !== undefined && usesRemaining === 1 && (
            <p className="chip-modal__status">
              Your final use unlocks at Gameweek {secondUseUnlockGameweek}.
            </p>
          )}
          <button
            className="chip-modal__action chip-modal__action--play"
            onClick={playConfirmation ? () => setConfirming(true) : onPlay}
          >
            Play for Gameweek {currentGameweek}
          </button>
        </>
      )}

      {chip.implemented && status === 'active' && (
        /* Wrapped, not two loose children: the group shrinks to its widest label so the
           Deactivate border stops where the Activated box's does. */
        <div className="chip-modal__active">
          <p className="chip-modal__status chip-modal__status--active">
            Activated
          </p>
          {!deadlinePassed && (
            <button
              className="chip-modal__action chip-modal__action--cancel"
              onClick={onCancel}
            >
              Deactivate
            </button>
          )}
        </div>
      )}

      {chip.implemented && status === 'deadlineLocked' && (
        <p className="chip-modal__status">
          Gameweek {currentGameweek}'s deadline has passed, so {chip.name} can
          no longer be played for this gameweek.
        </p>
      )}

      {chip.implemented && status === 'locked' && (
        <p className="chip-modal__status">
          {unlockGameweek !== undefined && currentGameweek < unlockGameweek
            ? `${chip.name} unlocks at Gameweek ${unlockGameweek}.`
            : blockingChipName
              ? `${blockingChipName} is already active this gameweek, so ${chip.name} can't be played alongside it.`
              : secondUseUnlockGameweek !== undefined
                ? `You've used your first ${chip.name}. Your second use unlocks at Gameweek ${secondUseUnlockGameweek}.`
                : `${chip.name} is locked this gameweek.`}
        </p>
      )}

      {chip.implemented && status === 'ineligible' && (
        <p className="chip-modal__status">
          {chip.name} is only available while you're in the bottom half of the
          Global League (ranked exactly at the median doesn't qualify). Check
          back once your rank changes.
        </p>
      )}

      {chip.implemented && status === 'cooldown' && (
        <p className="chip-modal__status">
          You played {chip.name} last gameweek — it can't be played two
          gameweeks in a row.
        </p>
      )}

      {chip.implemented && status === 'used' && (
        <p className="chip-modal__status">
          You've used {allUsesLabel(seasonCap)} {chip.name}
          {seasonCap > 1 ? 's' : ''} this season.
        </p>
      )}

      {/*. The chip circle that opens this modal is already an info affordance, so
          an ⓘ beside it would put two "explain this" targets on one row — the failure mode the
          ticket names. The handoff lives here instead, where someone is already asking.

          Left label, chevron right, muted: the same row treatment as the profile footer, and no
          colour of its own. "Play for Gameweek N" is the action on this sheet and keeps all the
          contrast. Close keeps its centred treatment below, with more space above it than the row
          has inside it, so the foot groups as [rules link] · · · [dismiss] rather than as two
          equal options. */}
      <SeeAllRulesRow
        onClick={() => {
          onClose()
          openRules('chips')
        }}
      />

      <button className="chip-modal__close" onClick={onClose}>
        Close
      </button>
    </ModalBackdrop>
  )
}

export default ChipInfoModal
